import { Icon, Row, TagTextAlert, Text } from "@bridgesplit/ui";
import { usePausedExternalYieldSources } from "@bridgesplit/abf-react";
import { ExternalYieldSource } from "@bridgesplit/abf-sdk";

function getExternalYieldSourceName(source: number | undefined): string {
    switch (source) {
        case ExternalYieldSource.MarginFi:
            return "MarginFi";
        default:
            return "external yield protocol";
    }
}

export function useIsPaused(source: number | undefined) {
    const { pausedExternalYieldSources } = usePausedExternalYieldSources();
    if (source === undefined || source === ExternalYieldSource.None || !pausedExternalYieldSources?.length)
        return false;
    return pausedExternalYieldSources.includes(source);
}

type AlertVariant = "strategy" | "strategyLoans" | "loan" | "market" | "vault" | "vaultWithdraw";

function getMessage(variant: AlertVariant, name: string): string {
    switch (variant) {
        case "strategy":
            return `Deposits are temporarily blocked until ${name} resumes normal protocol operations.`;
        case "strategyLoans":
            return `Loans funded by this strategy are supplied to ${name} and are temporarily frozen until ${name} resumes normal protocol operations.`;
        case "loan":
            return `This loan is borrowed from a strategy using ${name} and is temporarily frozen until ${name} resumes normal protocol operations.`;
        case "market":
            return `Strategies lending in this market may have idle liquidity deposited to ${name}, which is temporarily frozen until ${name} resumes normal protocol operations.`;
        case "vault":
            return `Withdrawals from this vault are temporarily unavailable until ${name} resumes normal protocol operations.`;
        case "vaultWithdraw":
            return `Withdrawals may be limited while ${name} is paused.`;
    }
}

export function PausedExternalYieldAlert({
    variant,
    source,
    fullWidth
}: {
    variant: AlertVariant;
    source: number | undefined;
    fullWidth?: boolean;
}) {
    const isPaused = useIsPaused(source);
    if (!isPaused) return null;
    const message = getMessage(variant, getExternalYieldSourceName(source));
    if (fullWidth)
        return (
            <Row spacing={1}>
                <Text variant="body2" color="warning">
                    <Icon type="warning" />
                    {message}
                </Text>
            </Row>
        );
    return <TagTextAlert>{message}</TagTextAlert>;
}

export function PausedVaultAlert({ vaultName, source }: { vaultName: string | undefined; source: number | undefined }) {
    const isPaused = useIsPaused(source);
    if (!isPaused || !vaultName) return null;
    const name = getExternalYieldSourceName(source);
    return (
        <TagTextAlert>{`Withdrawals from the ${vaultName} are temporarily unavailable until ${name} resumes normal protocol operations.`}</TagTextAlert>
    );
}
